import React from "react";
import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import PageShell from "../components/PageShell";
import { Section } from "../components/MarketingPrimitives";

const PrivacyPage = () => {
  return (
    <PageShell>
      <PageMeta
        title="Privacy Policy | BestSalesCoach.ai"
        description="Privacy Policy for BestSalesCoach.ai, a Victoria, Australia-based AI GTM Execution Intelligence platform."
        canonicalPath="/privacy"
      />

      <Section
        id="privacy-hero"
        eyebrow="Legal"
        title="Privacy Policy"
        description="This policy explains how BestSalesCoach.ai collects, uses, stores, discloses, and protects personal information."
        tone="hero"
      >
        <div className="marketing-card legal-card">
          <p className="marketing-body-copy">
            <strong>Effective date:</strong> 29 April 2026
          </p>
        </div>
      </Section>

      <Section id="privacy-content" tone="light" className="legal-section">
        <article className="marketing-card legal-card legal-prose">
          <p>BestSalesCoach.ai respects your privacy.</p>
          <p>
            This Privacy Policy explains how BestSalesCoach.ai ("BestSalesCoach.ai", "we", "us",
            or "our") collects, uses, stores, discloses, and protects personal information when
            you visit our website, submit a demo request, contact us, or use any related website
            features.
          </p>
          <p>BestSalesCoach.ai is operated from Victoria, Australia.</p>
          <p>
            By using this website or submitting your information, you acknowledge that we may
            handle your personal information as described in this Privacy Policy.
          </p>
          <p>
            Please also read our{" "}
            <Link className="marketing-text-link" to="/terms">
              Terms and Conditions
            </Link>
            , which govern use of this website and related services.
          </p>

          <h3>1. Personal information we collect</h3>
          <p>We may collect personal information that you provide directly, including:</p>
          <ul className="marketing-bullet-list">
            <li>name;</li>
            <li>email address;</li>
            <li>phone number, if provided;</li>
            <li>company name;</li>
            <li>role or title;</li>
            <li>company website;</li>
            <li>buyer type or area of interest;</li>
            <li>demo request details;</li>
            <li>message or enquiry content; and</li>
            <li>any other information you choose to provide.</li>
          </ul>
          <p>We may also collect website and source information, including:</p>
          <ul className="marketing-bullet-list">
            <li>page visited;</li>
            <li>CTA or form submitted;</li>
            <li>referral source;</li>
            <li>UTM or campaign parameters;</li>
            <li>approximate device/browser information;</li>
            <li>date and time of enquiry; and</li>
            <li>interaction or analytics information.</li>
          </ul>

          <h3>2. How we collect personal information</h3>
          <p>We may collect personal information when you:</p>
          <ul className="marketing-bullet-list">
            <li>submit a “Book a 20-Minute Demo” request;</li>
            <li>submit a services enquiry;</li>
            <li>contact us by email;</li>
            <li>interact with website forms, links, or CTAs;</li>
            <li>book a call or demo through a third-party form or calendar; or</li>
            <li>communicate with us about BestSalesCoach.ai.</li>
          </ul>

          <h3>3. Why we collect personal information</h3>
          <p>We collect and use personal information to:</p>
          <ul className="marketing-bullet-list">
            <li>respond to your enquiry;</li>
            <li>arrange, confirm, or follow up on a demo request;</li>
            <li>understand your GTM, revenue execution, or AI workflow needs;</li>
            <li>manage enquiries in our CRM;</li>
            <li>provide relevant information about BestSalesCoach.ai;</li>
            <li>improve our website, services, communications, and customer experience;</li>
            <li>manage business records and administration;</li>
            <li>protect the security and integrity of our website and systems; and</li>
            <li>comply with legal obligations where applicable.</li>
          </ul>

          <h3>4. Use of GoModern CRM and third-party providers</h3>
          <p>
            We may use third-party service providers, including GoModern CRM, to receive, store,
            process, and manage demo requests, enquiries, contact records, email notifications,
            scheduling, and follow-up communications.
          </p>
          <p>
            When you submit a demo request, your information may be sent to GoModern CRM or another
            service provider used to manage enquiries. Demo requests may also be forwarded to our
            nominated contact email:
          </p>
          <p>
            <strong>admin@bestsalescoach.ai</strong>
          </p>
          <p>
            We may also use third-party providers for website hosting, analytics, email,
            scheduling, CRM, security, and operational support.
          </p>

          <h3>5. Marketing communications</h3>
          <p>If you submit a demo request, we may contact you about that request.</p>
          <p>
            We may also send occasional updates about BestSalesCoach.ai, GTM Engineering services,
            AI GTM execution, or related content where permitted by law or where you have provided
            consent.
          </p>
          <p>
            You may unsubscribe from marketing communications at any time by using the unsubscribe
            option in the message or by contacting:
          </p>
          <p>
            <strong>admin@bestsalescoach.ai</strong>
          </p>
          <p>Marketing consent should be separate from consent to be contacted about a demo request.</p>

          <h3>6. Disclosure of personal information</h3>
          <p>We may disclose personal information to:</p>
          <ul className="marketing-bullet-list">
            <li>CRM, scheduling, email, hosting, analytics, and operational service providers;</li>
            <li>professional advisers where required;</li>
            <li>legal, regulatory, or government authorities where required by law;</li>
            <li>contractors or service providers who help us operate BestSalesCoach.ai; and</li>
            <li>other parties where you have consented or where disclosure is otherwise permitted by law.</li>
          </ul>
          <p>We do not sell personal information to advertisers.</p>

          <h3>7. Overseas disclosure</h3>
          <p>Some service providers we use may store or process information outside Australia.</p>
          <p>
            This may include CRM, hosting, email, analytics, or scheduling providers that operate
            internationally.
          </p>
          <p>
            Where we use overseas service providers, we take reasonable steps to use reputable
            providers and to limit information shared to what is needed for the relevant business
            purpose.
          </p>

          <h3>8. Security of personal information</h3>
          <p>
            We take reasonable steps to protect personal information from misuse, interference,
            loss, unauthorised access, modification, or disclosure.
          </p>
          <p>
            No method of transmission or storage is completely secure. You should avoid submitting
            sensitive information through website forms unless specifically requested.
          </p>

          <h3>9. Retention of personal information</h3>
          <p>
            We retain personal information for as long as reasonably necessary for the purposes
            described in this Privacy Policy, including responding to enquiries, managing CRM
            records, maintaining business records, and complying with legal obligations.
          </p>
          <p>
            When information is no longer required, we will take reasonable steps to delete,
            de-identify, or securely archive it, subject to business and legal requirements.
          </p>

          <h3>10. Access, correction, and deletion requests</h3>
          <p>
            You may contact us to request access to, correction of, or deletion of personal
            information we hold about you.
          </p>
          <p>We may need to verify your identity before processing your request.</p>
          <p>
            <strong>Contact:</strong>
          </p>
          <p>
            <strong>admin@bestsalescoach.ai</strong>
          </p>
          <p>We will respond within a reasonable time.</p>

          <h3>11. Cookies and analytics</h3>
          <p>
            Our website may use cookies, tracking parameters, analytics tools, or similar
            technologies to understand website usage, improve content, measure campaign
            performance, and support enquiry attribution.
          </p>
          <p>
            You can control cookies through your browser settings, but disabling cookies may affect
            some website functionality.
          </p>
          <p>
            Where we use third-party forms, calendars, CRMs, or embedded services, those
            third-party providers may use their own cookies or tracking technologies according to
            their own privacy policies.
          </p>

          <h3>12. AI-generated insights</h3>
          <p>
            BestSalesCoach.ai may use artificial intelligence to support GTM execution, pipeline
            review, stakeholder analysis, MEDDICC-related guidance, account strategy, and coaching
            prompts.
          </p>
          <p>
            AI-generated outputs are intended to support human judgement. They should not be
            treated as the sole basis for legal, financial, employment, or strategic decisions.
          </p>
          <p>
            Do not submit sensitive personal information, confidential customer data, or
            third-party information unless you have the right to do so and it is necessary for the
            relevant service.
          </p>

          <h3>13. Links to third-party websites</h3>
          <p>
            Our website may contain links to third-party websites, forms, calendars, CRMs, or
            other services.
          </p>
          <p>
            We are not responsible for the privacy practices, security, or content of third-party
            websites or services. You should review their privacy policies before providing
            information to them.
          </p>

          <h3>14. Changes to this Privacy Policy</h3>
          <p>We may update this Privacy Policy from time to time.</p>
          <p>The updated version will be published on this page with a revised effective date.</p>
          <p>
            Your continued use of the website after changes are published means you acknowledge the
            updated Privacy Policy.
          </p>

          <h3>15. Contact us</h3>
          <p>For questions about this Privacy Policy or your personal information, contact:</p>
          <p>
            BestSalesCoach.ai
            <br />
            Email: admin@bestsalescoach.ai
            <br />
            Location: Victoria, Australia
          </p>
        </article>
      </Section>
    </PageShell>
  );
};

export default PrivacyPage;
